import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { debounce } from "lodash";
import { toast } from "react-toastify";

// Services & Types
import { TrainingService } from "services/training.service";
import { MemberTrainingService } from "services/memberTraining.service";
import { MemberService } from "services/member.service";
import { ITrainingResponseDTO, IMemberResponseDTO } from "models/church.types";

interface MemberOption {
  value: number;
  label: string;
}

export default function TrainingList() {
  const [trainings, setTrainings] = useState<ITrainingResponseDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // --- ÉTATS POUR LA MODAL D'INSCRIPTION ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState<ITrainingResponseDTO | null>(null);
  const [memberOptions, setMemberOptions] = useState<MemberOption[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberOption | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [submittingRegistration, setSubmittingRegistration] = useState(false);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const loadData = async (query: string, page: number) => {
    setLoading(true);
    try {
      let result;
      if (query.trim()) {
        result = await TrainingService.search(query, page, itemsPerPage);
      } else {
        result = await TrainingService.getAll(page, itemsPerPage);
      }

      setTrainings(result.data || []);
      setTotalItems(result.totalCount || 0);
    } catch (error) {
      toast.error("Impossible de charger les formations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(searchTerm, currentPage);
  }, [currentPage]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      loadData(value, 0);
    }, 500),
    []
  );

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Supprimer cette formation définitivement ?")) {
      try {
        await TrainingService.delete(id);
        toast.success("Formation supprimée avec succès");
        loadData(searchTerm, currentPage);
      } catch (error) {
        toast.error("Erreur lors de la suppression de la formation.");
      }
    }
  };

  // --- LOGIQUE D'INSCRIPTION DIRECTE ---
  const handleOpenRegisterModal = (training: ITrainingResponseDTO) => {
    setSelectedTraining(training);
    setSelectedMember(null);
    setIsModalOpen(true);
    searchMembers(""); // Recherche initiale de membres
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTraining(null);
    setSelectedMember(null);
  };

  const searchMembers = async (query: string) => {
    setLoadingMembers(true);
    try {
      const res = await MemberService.search(query, 0, 20);
      const options = (res.data || []).map((m: IMemberResponseDTO) => ({
        value: m.id,
        label: `${m.firstName || ""} ${m.lastName || ""} ${m.phoneNumber ? `(${m.phoneNumber})` : ""}`.trim() || `Membre #${m.id}`,
      }));
      setMemberOptions(options);
    } catch (err) {
      console.error("Erreur chargement membres:", err);
    } finally {
      setLoadingMembers(false);
    }
  };

  const debouncedMemberSearch = useCallback(
    debounce((q: string) => searchMembers(q), 400),
    []
  );

const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTraining || !selectedMember) return;

    setSubmittingRegistration(true);
    try {
      await MemberTrainingService.create({
        memberId: selectedMember.value,
        trainingId: selectedTraining.id,
        completed: false,
      });

      toast.success(
        `Membre inscrit avec succès à la formation "${selectedTraining.title}"`
      );
      handleCloseModal();
    } catch (error: any) {
      const responseData = error.response?.data;

      // 1. Extraction de la clé d'erreur (compatible avec plusieurs formats Spring/JHipster)
      const errorKey =
        responseData?.errorKey ||
        responseData?.params?.errorKey ||
        responseData?.entityName;

      // 2. Extraction du message de détail s'il existe
      const detailMessage =
        responseData?.detail ||
        responseData?.title ||
        responseData?.message;

      // 3. Vérification de l'erreur spécifique "membre déjà inscrit"
      if (
        errorKey === "memberalreadyregistered" ||
        responseData?.message === "error.memberalreadyregistered" ||
        (detailMessage && detailMessage.toLowerCase().includes("déjà inscrit"))
      ) {
        toast.warning("Ce membre est déjà inscrit à cette formation.");
      } else {
        toast.error(
          detailMessage || "Erreur lors de l'inscription du membre."
        );
      }
    } finally {
      setSubmittingRegistration(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // --- STYLES REACT-SELECT MODAL ---
  const customSelectStyles = {
    control: (base: any) => ({
      ...base,
      backgroundColor: "#0F172A",
      borderColor: "#334155",
      color: "white",
      minHeight: "42px",
      borderRadius: "0.5rem",
    }),
    input: (base: any) => ({ ...base, color: "white" }),
    singleValue: (base: any) => ({ ...base, color: "white" }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1E293B",
      border: "1px solid #334155",
      zIndex: 9999,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? "#334155" : "transparent",
      color: "white",
      cursor: "pointer",
    }),
  };

  return (
    <div className="flex flex-col w-full px-6 min-h-screen">
      <div className="mb-6 pt-8">
        <h2 className="text-white text-3xl font-bold uppercase tracking-tight">
          Registre des Formations
        </h2>
        <p className="text-slate-400 text-sm italic">
          Gestion des cours, sessions d'enseignement et formations de l'église
        </p>
      </div>

      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="relative w-full md:w-96 group">
            <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-400 group-focus-within:text-[#D4AF37]">
              {loading ? (
                <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
              ) : (
                <i className="fas fa-search"></i>
              )}
            </span>
            <input
              type="text"
              value={searchTerm}
              onChange={onSearchChange}
              placeholder="Rechercher une formation..."
              className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg pl-11 pr-4 py-2 font-sans h-[38px] outline-none focus:border-blue-500 transition-colors"
            />
          </div>
        </div>
        <Link
          to="/admin/trainings/register"
          className="bg-[#D4AF37] hover:bg-[#b8952e] text-slate-900 font-bold px-8 h-[38px] flex items-center justify-center rounded-lg whitespace-nowrap transition-colors w-full lg:w-auto text-center text-sm"
        >
          <i className="fas fa-plus mr-2"></i> Ajouter une formation
        </Link>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden shadow-2xl mb-10">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#1E293B] text-slate-300 uppercase text-[10px] font-black border-b border-slate-700">
                <th className="px-6 py-5">ID</th>
                <th className="px-6 py-5">Titre</th>
                <th className="px-6 py-5">Instructeur</th>
                <th className="px-6 py-5 text-center">Date Début</th>
                <th className="px-6 py-5 text-center">Date Fin</th>
                <th className="px-6 py-5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {trainings.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-16 text-slate-500 italic text-sm"
                  >
                    <i className="fas fa-graduation-cap block text-3xl mb-3 opacity-20"></i>
                    Aucune formation trouvée.
                  </td>
                </tr>
              ) : (
                trainings.map((training) => (
                  <tr
                    key={training.id}
                    className="hover:bg-slate-700/40 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm text-white font-bold">
                      #{training.id}
                    </td>
                    <td className="px-6 py-4 text-xs font-bold text-white max-w-xs truncate">
                      {training.title}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-300 font-semibold">
                      {training.instructor || "Non spécifié"}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-400 text-center">
                      {formatDate(training.startDate)}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-400 text-center">
                      {formatDate(training.endDate)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-3">
                        {/* BOUTON D'INSCRIPTION D'UN MEMBRE */}
                        <button
                          onClick={() => handleOpenRegisterModal(training)}
                          title="Inscrire un membre"
                          className="text-slate-400 hover:text-emerald-400 transition-colors"
                        >
                          <i className="fas fa-user-plus"></i>
                        </button>
                        <Link
                          to={`/admin/trainings/edit/${training.id}`}
                          title="Modifier la formation"
                          className="text-slate-400 hover:text-[#D4AF37] transition-colors"
                        >
                          <i className="fas fa-edit"></i>
                        </Link>
                        <button
                          onClick={() => handleDelete(training.id)}
                          title="Supprimer la formation"
                          className="text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* SECTION PAGINATION */}
        <div className="px-6 py-5 bg-[#0F172A]/50 border-t border-slate-700 flex items-center justify-between">
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">
            Total global : <span className="text-[#D4AF37]">{totalItems}</span>{" "}
            formation(s)
          </p>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Précédent
            </button>
            <button
              disabled={currentPage + 1 >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>

      {/* MODAL D'INSCRIPTION RAPIDE */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#1E293B] border border-slate-700 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-fade-in">
            <div className="px-6 py-4 bg-slate-800 border-b border-slate-700 flex justify-between items-center">
              <h3 className="text-white text-base font-bold flex items-center gap-2">
                <i className="fas fa-user-plus text-emerald-400"></i> Inscrire
                un membre
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-slate-400 hover:text-white text-sm"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleRegisterSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">
                  Formation sélectionnée
                </label>
                <div className="bg-slate-900 text-emerald-400 border border-slate-700 rounded-lg p-3 text-sm font-bold">
                  {selectedTraining?.title}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">
                  Membre à inscrire <span className="text-red-500">*</span>
                </label>
                <Select
                  options={memberOptions}
                  value={selectedMember}
                  onChange={(opt) => setSelectedMember(opt)}
                  onInputChange={(val, action) => {
                    if (action.action === "input-change") {
                      debouncedMemberSearch(val);
                    }
                  }}
                  filterOption={() => true}
                  isLoading={loadingMembers}
                  placeholder="Rechercher par nom, prénom..."
                  styles={customSelectStyles}
                  isClearable
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-xs font-bold text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={!selectedMember || submittingRegistration}
                  className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold px-5 py-2 rounded-lg text-xs flex items-center gap-2 transition-colors"
                >
                  {submittingRegistration ? (
                    <i className="fas fa-circle-notch animate-spin"></i>
                  ) : (
                    <i className="fas fa-check"></i>
                  )}
                  Confirmer l'inscription
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}